'use client';

import * as React from 'react';
import { formatCurrency } from '@/shared/formatters/currency';
import { TextField, TextFieldRootProps } from './TextField';

interface CurrencyInputProps extends Omit<TextFieldRootProps, 'onChange' | 'value'> {
  value?: number;
  onChange?: (value: number) => void;
  placeholder?: string;
  /** Teto opcional: dígitos que ultrapassem este valor são descartados (input trava no máximo). */
  max?: number;
  decimais?: 2 | 3;
}

export const CurrencyInput = React.forwardRef<HTMLLabelElement, CurrencyInputProps>(
  function CurrencyInput({ value, onChange, placeholder, max, decimais = 2, ...props }, ref) {
    const fator = 10 ** decimais;

    // Formata o valor numérico para exibição (ex: 123.45 -> "123,45")
    const formatDisplayValue = (val: number | undefined) => {
      if (val === undefined || val === null) return '';
      return val.toLocaleString('pt-BR', {
        minimumFractionDigits: decimais,
        maximumFractionDigits: decimais,
      });
    };

    const [displayValue, setDisplayValue] = React.useState(formatDisplayValue(value));

    // Sincroniza estado interno quando a prop value muda externamente
    React.useEffect(() => {
      // Evita re-render loops se o valor formatado for igual ao atual
      // Mas precisamos garantir que updates externos reflitam
      if (value !== undefined) {
        setDisplayValue(formatDisplayValue(value));
      }
    }, [value, decimais]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      // Remove tudo que não é dígito
      const rawValue = e.target.value.replace(/\D/g, '');

      const numericValue = parseInt(rawValue || '0', 10);
      const floatValue = numericValue / fator;

      // Teto: ignora o dígito que ultrapassa o máximo — mantém o valor anterior
      // (não "salta" para o teto). O input controlado descarta a tecla extra.
      if (max !== undefined && floatValue > max) {
        return;
      }

      // Atualiza o display visualmente formatado
      setDisplayValue(formatDisplayValue(floatValue));

      // Propaga o valor numérico real (em float/decimal) para o pai
      if (onChange) {
        onChange(floatValue);
      }
    };

    return (
      <TextField ref={ref} {...props}>
        <div className="relative h-full w-full">
          <span className="absolute top-1/2 left-0 -translate-y-1/2 text-neutral-500">R$</span>
          <TextField.Input
            type="text"
            inputMode="numeric"
            className="pl-7" // Espaço para o prefixo R$
            value={displayValue}
            onChange={handleChange}
            placeholder={placeholder ?? (decimais === 3 ? '0,000' : '0,00')}
          />
        </div>
      </TextField>
    );
  },
);
